import { useEffect, useRef } from "react";
import { ShieldCheck } from "lucide-react";

import AuthPage from "@/pages/AuthPage";
import { useAuth } from "@/lib/authContext";

/**
 * AuthCallback — synchronously handles the `#session_id=…` redirect from
 * Emergent's Google OAuth. Uses a useRef latch (not useState) so StrictMode's
 * double-invoke doesn't double-submit the one-time session_id.
 */
function AuthCallback({ sessionId }) {
    const { exchangeGoogleSession } = useAuth();
    const processed = useRef(false);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;
        exchangeGoogleSession(sessionId)
            .then(() => {
                // Clean the URL so a refresh doesn't replay the callback.
                window.history.replaceState(
                    {},
                    "",
                    window.location.pathname + window.location.search,
                );
            })
            .catch(() => {
                // Bad/expired session_id — fall through to the AuthPage so the
                // user can try again. Clean URL so AuthGate doesn't loop.
                window.history.replaceState(
                    {},
                    "",
                    window.location.pathname + window.location.search,
                );
            });
    }, [sessionId, exchangeGoogleSession]);

    return <Splash label="Signing you in…" />;
}

/**
 * Splash shown while AuthProvider probes /api/auth/me. Keeps the brand mark
 * visible so the transition into the chat view feels continuous, not jarring.
 */
function Splash({ label = "Loading…" }) {
    return (
        <div
            data-testid="auth-splash"
            className="min-h-screen flex flex-col items-center justify-center gap-4 text-foreground bg-background"
        >
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <div className="text-sm opacity-60">{label}</div>
        </div>
    );
}

/**
 * AuthGate — gates the entire app behind a resolved authenticated user.
 *
 * - On mount, AuthProvider's effect calls /api/auth/me. While `user === null`
 *   we show the splash to avoid a flash of the AuthPage for already-logged-in
 *   users.
 * - If the URL hash contains `session_id=`, we render AuthCallback instead of
 *   probing /me — preventing the global race the Emergent playbook warns about.
 * - Once user resolves to `false`, show AuthPage. Once it's a real user,
 *   render `children` (the existing ToneGuard app).
 */
export default function AuthGate({ children }) {
    const { user, cloudSyncState } = useAuth();

    // Detect Emergent OAuth callback synchronously during render (NOT in
    // useEffect) so we beat the AuthProvider's /me probe.
    const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
    if (hash.includes("session_id=")) {
        const m = hash.match(/session_id=([^&]+)/);
        if (m && m[1]) {
            return <AuthCallback sessionId={decodeURIComponent(m[1])} />;
        }
    }

    if (user === null) return <Splash label="Checking session…" />;
    if (!user) return <AuthPage />;

    // After login, wait for the cloud-sync bootstrap to finish so the
    // hydrated localStorage is in place BEFORE protected components mount
    // and read it. Otherwise the first render shows stale local data.
    if (cloudSyncState === "syncing") {
        return <Splash label="Syncing your data…" />;
    }

    // Force a clean remount of the protected tree when the user changes so
    // any in-memory state from a previous account is fully dropped.
    return <div key={user.user_id || "anon"}>{children}</div>;
}
